import { BaseNodeCommand, type NodeChangeRecord } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { getTableCellRectByKey } from '../../domains/diagram/shapes/table/tableDrop.utils';
import { getTableCellLayoutAlgorithm, resolveCellLayoutConfig } from '../../domains/diagram/shapes/table/tableCellLayout.registry';

type TableDock = { tableId: string; cellKey: `${number},${number}`; offset?: { x: number; y: number } };

type MergedRegion = { startRow: number; startCol: number; endRow: number; endCol: number };
const containsCell = (r: MergedRegion, row: number, col: number) => {
  return row >= r.startRow && row <= r.endRow && col >= r.startCol && col <= r.endCol;
};

const parseKey = (k: `${number},${number}`) => {
  const [rStr, cStr] = String(k).split(',');
  return { r: Number(rStr), c: Number(cStr) };
};

export class ReflowDockedNodesInTableCellCommand extends BaseNodeCommand {
  constructor(private tableId: string, private cellKey: `${number},${number}`) {
    super('Reflow Cell Layout', `Reflow docked content in ${cellKey}`);
  }

  protected doExecute(): NodeChangeRecord[] {
    const mm: any = this.getMutationManager();
    const store = useDiagramStore.getState();
    const tableNode = store.nodes.find((n) => n.id === this.tableId) as any;
    if (!tableNode || tableNode.type !== 'table') return [];

    const hit = getTableCellRectByKey(tableNode, this.cellKey);
    if (!hit) return [];

    const attrs = (tableNode.attrs ?? {}) as Record<string, unknown>;
    const mergedRegions = (Array.isArray((attrs as any).mergedRegions) ? ((attrs as any).mergedRegions as MergedRegion[]) : []) ?? [];
    const anchorKey = hit.cellKey;
    const anchorParsed = parseKey(anchorKey);
    const anchorMr = mergedRegions.find((m) => containsCell(m, anchorParsed.r, anchorParsed.c)) ?? null;

    const docked = store.nodes.filter((n) => {
      const dock = (n as any)?.attrs?.tableDock as TableDock | undefined;
      if (!dock) return false;
      if (dock.tableId !== this.tableId) return false;
      if (dock.cellKey === anchorKey) return true;
      if (!anchorMr) return false;
      const p = parseKey(dock.cellKey);
      return Number.isFinite(p.r) && Number.isFinite(p.c) && containsCell(anchorMr, p.r, p.c);
    });

    if (docked.length === 0) return [];

    const config = resolveCellLayoutConfig(attrs, anchorKey as any);
    const algorithm = getTableCellLayoutAlgorithm(config.algorithmId);
    const layoutRect = hit.contentRect;

    const items = docked
      .slice()
      .sort((a: any, b: any) => {
        const za = Number((a as any).zIndex ?? 0);
        const zb = Number((b as any).zIndex ?? 0);
        if (za !== zb) return za - zb;
        return String(a.id).localeCompare(String(b.id));
      })
      .map((n: any) => {
        const w = (n.w ?? n.width ?? 60) as number;
        const h = (n.h ?? n.height ?? 60) as number;
        const dock = (n as any)?.attrs?.tableDock as TableDock | undefined;
        return {
          id: n.id as string,
          w: Number(w) || 60,
          h: Number(h) || 60,
          x: Number(n.x) || 0,
          y: Number(n.y) || 0,
          dock,
        };
      });

    const itemsForAlgorithm =
      config.algorithmId === 'fixed'
        ? items.map((it) => {
            const offX =
              typeof it.dock?.offset?.x === 'number' && Number.isFinite(it.dock.offset.x) ? it.dock.offset.x : it.x - layoutRect.x;
            const offY =
              typeof it.dock?.offset?.y === 'number' && Number.isFinite(it.dock.offset.y) ? it.dock.offset.y : it.y - layoutRect.y;
            return { id: it.id, w: it.w, h: it.h, x: offX, y: offY };
          })
        : items.map((it) => ({ id: it.id, w: it.w, h: it.h, x: it.x, y: it.y }));

    const next = algorithm.arrange({ cellRect: layoutRect, items: itemsForAlgorithm, params: config.params });

    const updates: Array<{ nodeId: string; changes: any }> = [];
    for (const it of items) {
      const p = next[it.id];
      if (!p) continue;
      if (config.algorithmId === 'fixed' && it.dock) {
        updates.push({
          nodeId: it.id,
          changes: { x: p.x, y: p.y, attrs: { tableDock: { ...it.dock, offset: { x: p.x - layoutRect.x, y: p.y - layoutRect.y } } } },
        });
      } else {
        updates.push({ nodeId: it.id, changes: { x: p.x, y: p.y } });
      }
    }

    if (updates.length === 0) return [];
    if (updates.length > 1 && typeof mm.applyBatchNodeChanges === 'function') {
      return mm.applyBatchNodeChanges(updates) ?? [];
    }
    const rec = mm.applyNodeChange(updates[0].nodeId, updates[0].changes);
    return rec ? [rec] : [];
  }
}
